import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {ModalType, ShareType, SocialNetwork} from "data/enums";
import type {IShareStore} from "data/stores/share/share.store";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {copyToClipboard, share} from "data/utils";
import type {IUserStore} from "data/stores/user/user.store";
import type {IModalsStore, IModalPayload} from "data/stores/modals/modals.store";
import {SITE_URL} from "data/constants";
import {copyImageToClipboard} from "copy-image-clipboard";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";

export interface IShareController extends ViewController {
	readonly i18n: ILocalizationStore;
	get isMobileShareVisible(): boolean;
	get isOpen(): boolean;
	get modalContent(): IModalPayload | null;
	get shareImagePath(): string;
	get isCodeCopied(): boolean;

	shareViaFB: () => void;
	shareViaTW: () => void;
	shareViaMobile: () => void;
	close: () => void;
	onCopyImage: () => void;
	downloadImage: (src: string) => void;
}

@injectable()
export class ShareController implements IShareController {
	@observable private _isNarrowScreen: boolean = false;
	@observable private _isCodeCopied = false;
	@observable private _copyCodeTextTimeout?: ReturnType<typeof setTimeout>;

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.SHARE_PICKS;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get isCodeCopied() {
		return this._isCodeCopied;
	}

	get shareImagePath() {
		return this._shareStore.shareImagePath;
	}

	get isMobileShareVisible() {
		return "share" in navigator && this._isNarrowScreen;
	}

	private get narrowScreenWatcher() {
		return window.matchMedia("(max-width: 960px)");
	}
	constructor(
		@inject(Bindings.UserStore) public _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.ShareStore) public _shareStore: IShareStore,
		@inject(Bindings.ModalsStore) public _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	private share(socialNetwork: SocialNetwork) {
		share({
			socialNetwork,
			userId: this._userStore.user?.id || 0,
			type: ShareType.General,
			lang: this.i18n.lang,
			message: this.i18n.t("modal.share.message", "Test message"),
		});
	}

	shareViaFB = () => {
		this.share(SocialNetwork.Facebook);
		createConnextraScript(ConnextraType.SHARE_PICKS, this._userStore.user?.id);
	};

	shareViaTW = () => {
		this.share(SocialNetwork.Twitter);
		createConnextraScript(ConnextraType.SHARE_PICKS, this._userStore.user?.id);
	};

	shareViaMobile = () => {
		this.share(SocialNetwork.Mobile);
		createConnextraScript(ConnextraType.SHARE_PICKS, this._userStore.user?.id);
	};

	close = () => {
		this._modalsStore.hideModal();
	};

	@action private onCopyCodeState = () => {
		this._isCodeCopied = false;
	};

	@action private onCopyCodeCallback = (isSuccess: boolean) => {
		this._isCodeCopied = isSuccess;

		if (isSuccess) {
			this._copyCodeTextTimeout = setTimeout(this.onCopyCodeState, 5000);
		}
	};

	onCopyImage = () => {
		const imagePath = `${SITE_URL}${this.shareImagePath}`;

		copyToClipboard(imagePath).then(this.onCopyCodeCallback).catch(this.onCopyCodeCallback);
	};

	downloadImage = (src: string) => {
		if (!src) return;

		copyImageToClipboard(src)
			.then(() => this.onCopyCodeCallback(true))
			.catch((err) => {
				console.log("Error: ", err);
			});

		// const img = await fetch(src);
		// const imgBlob = await img.blob();
		//
		// try {
		// 	await navigator.clipboard
		// 		.write([
		// 			new ClipboardItem({
		// 				"image/png": imgBlob, // change image type accordingly
		// 			}),
		// 		])
		// 		.then(() => this.onCopyCodeCallback);
		// } catch (error) {
		// 	console.error(error);
		// }
	};

	@action private updateNarrowScreenFlag = () => {
		this._isNarrowScreen = this.narrowScreenWatcher.matches;
	};

	dispose(): void {
		this.narrowScreenWatcher.removeEventListener("change", this.updateNarrowScreenFlag);
		if (this._copyCodeTextTimeout) clearTimeout(this._copyCodeTextTimeout);
	}

	init(): void {
		this.updateNarrowScreenFlag();
		this.narrowScreenWatcher.addEventListener("change", this.updateNarrowScreenFlag);
	}
}
